import React, { useEffect } from 'react';
import { clsx } from 'clsx';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useGetAllProjectsQuery } from 'src/services';
import './Menu.css';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { PATH_DASHBOARD } from 'src/constants/spa-routes';
import { SidebarPlusSquareIcon } from 'src/assets/icons/sidebar/sidebarPlusSquareIcon';
import { SidebarArrowIcon } from 'src/assets/icons/sidebar/sidebarArrowIcon';
import { useAppDispatch, useAppSelector } from 'src/store';
import { COSSUPORT_DEMO_PAGE, LATEST_VIEWED_PROJECT_ID } from 'src/shared/constants';
import { setProjectId } from 'src/store/slices/projectsSlices';
import { ProductsType, ProductsTypeBack } from 'src/constants/products';
import { menuItemsProducts } from 'src/components/Menu/menuItems';
import { Input } from 'src/components/ui/input';
import { highlightMatchHelper } from 'src/helpers/highlightMatchHelper';
import { useGetAccountsQuery } from 'src/services/accountsService';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import type { ProductKey } from 'src/constants/products';
import type { Project } from 'src/services/projectsService';
import { useIsCosupportManager } from 'src/hooks/useIsCosupportManager';

const MAX_PROJECTS_LENGTH = 8;

const getProjectLink = ({ id, project_type }: Project) =>
  project_type === ProductsType.customer
    ? `${PATH_DASHBOARD.PROJECTS}/${id}/control-desk?type=${project_type}`
    : `${PATH_DASHBOARD.PROJECTS}/${id}?type=${project_type}`;

// Initials of the first two words, or the first two letters of a one-word name.
const getShortName = (name: string) => {
  const words = name.trim().split(/\s+/);

  return words.length > 1
    ? words
        .map((word) => word[0])
        .slice(0, 2)
        .join('')
    : name.trim().slice(0, 2);
};

// Non-customer products are request-only for anyone but CoSupport managers.
const canAddDirectly = (type: ProductKey, isCosupportManager: boolean) =>
  type === ProductsType.customer || isCosupportManager;

const getAddProjectTarget = (type: ProductKey, isCosupportManager: boolean, isBlocked: boolean) =>
  isBlocked || canAddDirectly(type, isCosupportManager) ? '_self' : '_blank';

const getAddProjectLink = (type: ProductKey, isCosupportManager: boolean) => {
  if (type === ProductsType.customer) return `${PATH_DASHBOARD.PROJECTS_NEW_STEP_1}?type=${type}`;
  return isCosupportManager ? `${PATH_DASHBOARD.PROJECTS_NEW}?type=${type}` : COSSUPORT_DEMO_PAGE;
};

export const ProductsItems = ({ className }: { className?: string }) => {
  const { pathname } = useLocation();
  const [searchValue, setSearchValue] = React.useState('');
  const { data: projects = [] } = useGetAllProjectsQuery();
  const [filteredProjects, setFilteredProjects] = React.useState(projects);
  const { id } = useParams();
  const { storageMenuOpen } = useAppSelector((s) => s.menuSlices);
  const { data: accounts } = useGetAccountsQuery();

  const dispatch = useAppDispatch();

  const changeProject = (projectId: number, projectType: ProductKey) => {
    localStorage.setItem(LATEST_VIEWED_PROJECT_ID, String(projectId));
    dispatch(setProjectId({ projectId, projectType }));
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const addProjectHandler = (
    event: React.MouseEvent,
    projectType: ProductKey,
    isProjectsLimitReached: boolean,
  ) => {
    if (isProjectsLimitReached) {
      event.preventDefault();
      return;
    }

    dispatch(setProjectId({ projectType }));
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const projectsByType = filteredProjects?.reduce<Record<ProductKey, Project[]>>(
    (acc, project) => {
      if (!acc[project?.project_type]) {
        acc[project.project_type] = [];
      }
      acc[project?.project_type].push(project);
      return acc;
    },
    { agent: [], bi: [], customer: [] },
  );

  const isCosupportManager = useIsCosupportManager();

  const menuItemsProductsFilterByProjects = menuItemsProducts.filter((item) => {
    if (item.type !== ProductsType.customer) {
      return projectsByType[item.type].length;
    }
    return item;
  });

  const { isInactivePlan } = useAppSelector((s) => s.planSlice);

  useEffect(() => {
    if (!searchValue) {
      if (projects.length) {
        filteredProjects !== projects && setFilteredProjects(projects);
      } else {
        filteredProjects.length && setFilteredProjects([]);
      }
    } else {
      const newFilteredProjects = projects.filter((project) =>
        project.name.toLowerCase().includes(searchValue.toLowerCase()),
      );

      setFilteredProjects(newFilteredProjects);
    }

    if (projects.length < MAX_PROJECTS_LENGTH && searchValue) {
      setSearchValue('');
    }
  }, [projects, searchValue]);

  return (
    <div className={clsx(storageMenuOpen ? 'px-6' : 'px-4', 'py-5 min-w-[70px]', className)}>
      {projects.length > MAX_PROJECTS_LENGTH && (
        <div className={'flex flex-col w-full pb-5'}>
          <Input
            type={'text'}
            onChange={(e) => setSearchValue(e.target.value)}
            className={
              'bg-ebony_opacity_50 transition-colors text-white border-shark focus:border-white placeholder:text-shark focus:placeholder:text-white'
            }
            placeholder={'Search'}
          />
        </div>
      )}
      <div className="flex flex-col gap-2.5">
        {menuItemsProductsFilterByProjects?.map((menuItem, index) => {
          const isActiveTab = projectsByType[menuItem.type].some((el) => {
            return el.id === +id;
          });
          const generalPlan = accounts?.general_plan;
          const maxProjects = generalPlan
            ? generalPlan[`max_projects_${ProductsTypeBack[menuItem.type]}`]
            : Infinity;
          const projectsCount = projectsByType[menuItem.type]?.length ?? 0;
          const isProjectsLimitReached = projectsCount >= maxProjects;

          return (
            <Accordion
              key={`${menuItem.id}-${index}`}
              type="single"
              collapsible
              className="w-full"
              defaultValue={menuItem.title}
            >
              <AccordionItem value={menuItem.title} className="border-b-0">
                <AccordionTrigger
                  hideDefaultIcon
                  className={clsx(
                    isActiveTab ? '[&_p]:opacity-100' : '[&_p]:opacity-60',
                    '[&_p]:hover:opacity-100 [&_svg:last-child]:hover:opacity-100',
                    'py-0 rounded-sm-5 duration-300 [&_p]:transition-all [&_svg]:transition-all [&[data-state=open]_p]:opacity-100 [&[data-state=open]_svg]:opacity-100 [&_svg:last-child]:opacity-50',
                    !storageMenuOpen && '[&_.chevron]:!opacity-0',
                  )}
                >
                  <div className="flex items-center gap-1">
                    <div className="p-2">{menuItem.icon}</div>
                    <p
                      className={clsx(
                        !storageMenuOpen && '!opacity-0',
                        'transition-all absolute left-16 duration-300 text-white text-sm font-medium text-ellipsis overflow-hidden text-nowrap',
                      )}
                    >
                      {menuItem.title}
                    </p>
                  </div>
                  <SidebarArrowIcon className="chevron transition-transform duration-200" />
                </AccordionTrigger>
                <AccordionContent className="pb-0">
                  <div className="mt-2 flex flex-col gap-1.5 relative">
                    {projectsByType[menuItem.type]?.map((el) => {
                      const isProjectPage = pathname.startsWith(PATH_DASHBOARD.PROJECTS);
                      const isSameId = +id === el.id;
                      const activeSlide = isProjectPage && isSameId;
                      const cutName = getShortName(el.name);
                      return (
                        <Link
                          onClick={() => changeProject(el.id, el?.project_type)}
                          key={el.id}
                          className={clsx(
                            storageMenuOpen && 'pl-11 pr-2.5',
                            activeSlide && 'bg-[#1E1E25]',
                            'p-1 rounded-sm-5 overflow-hidden transition-all duration-300 [&_span]:hover:opacity-100 [&_span]:transition-all relative',
                          )}
                          to={getProjectLink(el)}
                        >
                          <div
                            className={clsx(
                              activeSlide ? '[&_span]:opacity-100' : '[&_span]:opacity-60',
                              ' transition-all duration-300',
                            )}
                          >
                            <button
                              className={clsx(
                                !storageMenuOpen && 'bg-white bg-opacity-10',
                                storageMenuOpen && 'truncate',
                                'p-1.5 text-left max-w-full rounded-sm-5 min-w-[30px]',
                              )}
                              title={el.name}
                            >
                              <span
                                className={clsx(
                                  'text-white',
                                  activeSlide && 'font-bold',
                                  'font-medium text-sm',
                                )}
                              >
                                {storageMenuOpen
                                  ? highlightMatchHelper(el.name, searchValue)
                                  : cutName}
                              </span>
                            </button>
                          </div>
                        </Link>
                      );
                    })}
                    <Link
                      target={getAddProjectTarget(
                        menuItem.type,
                        isCosupportManager,
                        isInactivePlan || isProjectsLimitReached,
                      )}
                      onClick={(event) =>
                        addProjectHandler(event, menuItem.type, isProjectsLimitReached)
                      }
                      to={
                        isInactivePlan ? '' : getAddProjectLink(menuItem.type, isCosupportManager)
                      }
                      className={clsx(
                        isInactivePlan && 'pointer-events-none',
                        storageMenuOpen && 'pl-2.5 pr-2.5',
                        'p-1 w-full opacity-70 hover:opacity-100 transition-all duration-300',
                      )}
                    >
                      <button
                        disabled={isProjectsLimitReached}
                        className={clsx(
                          isInactivePlan && 'pointer-events-none opacity-20',
                          !storageMenuOpen && 'bg-white bg-opacity-10 p-2',
                          isProjectsLimitReached && 'cursor-not-allowed',
                          'flex min-w-[30px] min-h-[30px] items-center justify-start p-1 gap-2 rounded-sm-5 transition-all duration-300',
                        )}
                      >
                        <SidebarPlusSquareIcon color={menuItem.accentHex} />
                        <span
                          className={clsx(
                            !storageMenuOpen && '!opacity-0',
                            menuItem.color,
                            'transition-all absolute left-[40px] duration-300 text-[13px] font-semibold',
                          )}
                        >
                          {canAddDirectly(menuItem.type, isCosupportManager)
                            ? `Add ${menuItem.addLabel}`
                            : 'Request access'}
                        </span>
                        {isProjectsLimitReached && (
                          <InputInfoToolTip
                            wrapperClassName={'[&_path]:fill-white right-0 absolute cursor-pointer'}
                            infoToolTip={"The project's limit is reached"}
                          />
                        )}
                      </button>
                    </Link>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          );
        })}
      </div>
    </div>
  );
};
